import FbButton from 'components/FbUI/FbButton'
import apiService from 'services/api'
import { useState, useEffect } from 'react'
import { Alert } from 'react-bootstrap'
import { FbLink } from 'components/FbUI/FbLink'

interface CompanyData {
  id: number
  name: string
  email_extension: string
  billing_email: string | null
  stripe_customer_id: string | null
}

export function Billing() {
  const api = apiService()
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [companyData, setCompanyData] = useState<CompanyData | null>(null)
  const [newBillingEmail, setNewBillingEmail] = useState('')
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    fetchCompanyData()
  }, [])

  async function fetchCompanyData() {
    setIsLoading(true)
    try {
      const data = await api.getCompanyDetails()
      setCompanyData(data)
      setIsLoading(false)
    } catch (error) {
      console.error('Error fetching company data:', error)
      setIsLoading(false)
    }
  }

  async function openStripePortal() {
    setIsLoading(true)
    try {
      const url = await api.getStripePortalRedirect(window.location.href)
      window.location.href = url
    } finally {
      setIsLoading(false)
    }
  }

  async function saveBillingEmail() {
    if (!newBillingEmail) return

    setIsSaving(true)
    setSaveSuccess(false)
    setSaveError(null)

    try {
      await api.updateBillingEmail(newBillingEmail)
      await fetchCompanyData()
      setSaveSuccess(true)
      setNewBillingEmail('')
    } catch (error: any) {
      console.error('Error saving billing email:', error)
      setSaveError(
        error.response?.data?.error || 'Failed to save billing email'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewBillingEmail(e.target.value)
    setSaveSuccess(false)
    setSaveError(null)
  }

  console.log(companyData)

  return (
    <div className="flex flex-col items-center w-full">
      <div className="max-w-2xl w-full px-4 py-6">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold mb-4">Billing Settings</h2>
          <p className="text-gray-600 mb-4">
            Manage your billing information and subscription details here.
          </p>
          <div className="flex justify-center">
            <FbButton
              onClick={() => openStripePortal()}
              loading={isLoading}
              disabled={!companyData?.billing_email}
              size="md"
            >
              Manage Billing
            </FbButton>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center text-gray-600 py-6">
            Loading billing information...
          </div>
        ) : (
          <div className="bg-white p-6">
            <h3 className="text-lg font-semibold mb-4 text-center">
              Billing Email
            </h3>

            {companyData?.billing_email ? (
              <div className="text-center">
                <p className="text-gray-600 mb-3">
                  Current billing email:{' '}
                  <strong>{companyData.billing_email}</strong>
                </p>
                <p className="text-gray-600">
                  To update your billing email, please contact us at{' '}
                  <FbLink to="mailto:support@firstbite.io">
                    support@firstbite.io
                  </FbLink>
                  .
                </p>
              </div>
            ) : (
              <>
                <p className="text-gray-600 mb-4 text-center">
                  Set your billing email
                </p>
                <div className="max-w-md mx-auto">
                  <div className="mb-4">
                    <label
                      htmlFor="billing-email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Billing Email
                    </label>
                    <input
                      id="billing-email"
                      type="email"
                      value={newBillingEmail}
                      onChange={handleEmailChange}
                      placeholder="Enter billing email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex justify-center">
                    <FbButton
                      onClick={saveBillingEmail}
                      loading={isSaving}
                      disabled={!newBillingEmail}
                      size="md"
                    >
                      Save
                    </FbButton>
                  </div>
                  {saveSuccess && (
                    <Alert variant="success" className="mt-4 text-center">
                      Billing email saved successfully.
                    </Alert>
                  )}
                  {saveError && (
                    <Alert variant="danger" className="mt-4 text-center">
                      {saveError}
                    </Alert>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
